import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  HelpCircle, 
  MessageSquare, 
  Phone, 
  Mail, 
  FileText, 
  Search, 
  ChevronRight, 
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Book,
  Settings,
  Zap
} from 'lucide-react';
import ContentLayout from '../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  padding: 50px 30px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 40px;
`;

const HeroTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 15px 0;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;
  margin: 0 0 30px 0;
  line-height: 1.5;
`;

const SearchContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 50px 15px 20px;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #1f2937;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: #1d4ed8;
  }
`;

const QuickLinksSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 30px;
  margin-bottom: 30px;
`;

const QuickLinksTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  text-align: center;
`;

const QuickLinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

const QuickLinkCard = styled.div`
  background: ${props => props.bgColor || '#f9fafb'};
  border: 2px solid ${props => props.borderColor || 'transparent'};
  border-radius: 12px;
  padding: 25px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    border-color: ${props => props.hoverColor || '#3b82f6'};
  }
`;

const QuickLinkIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.iconBg || '#3b82f6'};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
`;

const QuickLinkTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const QuickLinkDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 25px;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.bgColor || '#3b82f6'};
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const InfoContent = styled.div`
  color: #4b5563;
  line-height: 1.6;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
`;

const ContactIcon = styled.div`
  color: #3b82f6;
  display: flex;
  align-items: center;
`;

const ContactText = styled.div`
  flex: 1;
`;

const ContactLabel = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 2px 0;
`;

const ContactValue = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const FAQSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  padding: 30px;
`;

const FAQTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
  text-align: center;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FAQItem = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const FAQQuestion = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin: 0;
  flex: 1;
`;

const FAQCategory = styled.span`
  background: #dbeafe;
  color: #1d4ed8;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 10px;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 30px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 20px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.color || '#1f2937'};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;

function Support() {
  const [searchQuery, setSearchQuery] = useState('');

  const quickLinks = [
    {
      title: '자주 묻는 질문',
      description: '가장 많이 문의하시는 내용들을 확인하세요',
      icon: HelpCircle,
      bgColor: '#dbeafe',
      borderColor: '#3b82f6',
      hoverColor: '#2563eb',
      iconBg: '#3b82f6',
      link: '/support/faq'
    },
    {
      title: '1:1 문의',
      description: '개인적인 문의사항을 남겨주세요',
      icon: MessageSquare,
      bgColor: '#ecfdf5',
      borderColor: '#10b981',
      hoverColor: '#059669',
      iconBg: '#10b981',
      link: '/support/contact'
    },
    {
      title: '전화 상담',
      description: '전화로 직접 상담받으실 수 있습니다',
      icon: Phone,
      bgColor: '#fef3c7',
      borderColor: '#f59e0b',
      hoverColor: '#d97706',
      iconBg: '#f59e0b',
      link: '/support/phone'
    },
    {
      title: '이용 가이드',
      description: '서비스 이용 방법을 단계별로 안내합니다',
      icon: Book,
      bgColor: '#fce7f3',
      borderColor: '#ec4899',
      hoverColor: '#db2777',
      iconBg: '#ec4899',
      link: '/support/guide'
    }
  ];

  const frequentFAQs = [
    { question: '주문한 상품은 언제 배송되나요?', category: '배송' },
    { question: '반품/교환은 어떻게 하나요?', category: '반품교환' },
    { question: '쿠폰은 어떻게 사용하나요?', category: '쿠폰' },
    { question: '적립금은 언제 지급되나요?', category: '적립금' },
    { question: '회원가입 시 인증이 안 돼요', category: '회원' },
    { question: '결제가 실패했는데 어떻게 하나요?', category: '결제' }
  ];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      console.log('Searching for:', searchQuery);
      window.location.href = `/support/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleQuickLink = (link) => {
    window.location.href = link;
  };

  const handleFAQClick = (faq) => {
    console.log('FAQ clicked:', faq);
    window.location.href = '/support/faq';
  };

  return (
    <ContentLayout 
      title="고객센터"
      subtitle="궁금한 점이나 문의사항이 있으시면 언제든지 연락해 주세요"
    >
      <Container>
        <HeroSection>
          <HeroTitle>무엇을 도와드릴까요?</HeroTitle>
          <HeroSubtitle>
            궁금한 내용을 검색하거나 아래 메뉴를 이용해 주세요
          </HeroSubtitle>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="궁금한 내용을 검색해보세요..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <SearchButton onClick={handleSearch}>
              <Search size={18} />
            </SearchButton>
          </SearchContainer>
        </HeroSection>

        <QuickLinksSection>
          <QuickLinksTitle>원하는 서비스를 선택해주세요</QuickLinksTitle>
          <QuickLinksGrid>
            {quickLinks.map((link, index) => (
              <QuickLinkCard
                key={index}
                bgColor={link.bgColor}
                borderColor={link.borderColor}
                hoverColor={link.hoverColor}
                onClick={() => handleQuickLink(link.link)}
              >
                <QuickLinkIcon iconBg={link.iconBg}>
                  <link.icon size={24} />
                </QuickLinkIcon>
                <QuickLinkTitle>{link.title}</QuickLinkTitle>
                <QuickLinkDescription>{link.description}</QuickLinkDescription>
              </QuickLinkCard>
            ))}
          </QuickLinksGrid>
        </QuickLinksSection>

        <InfoSection>
          <InfoCard>
            <InfoHeader>
              <InfoIcon bgColor="#10b981">
                <Clock size={20} />
              </InfoIcon>
              <InfoTitle>운영 시간</InfoTitle>
            </InfoHeader>
            <InfoContent>
              <ContactInfo>
                <ContactItem>
                  <ContactIcon>
                    <Clock size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>평일</ContactLabel>
                    <ContactValue>오전 9시 - 오후 6시</ContactValue>
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIcon>
                    <Clock size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>토요일</ContactLabel>
                    <ContactValue>오전 9시 - 오후 1시</ContactValue>
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIcon>
                    <AlertCircle size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>일요일/공휴일</ContactLabel>
                    <ContactValue>휴무</ContactValue>
                  </ContactText>
                </ContactItem>
              </ContactInfo>
            </InfoContent>
          </InfoCard>

          <InfoCard>
            <InfoHeader>
              <InfoIcon bgColor="#3b82f6">
                <Phone size={20} />
              </InfoIcon>
              <InfoTitle>연락처</InfoTitle>
            </InfoHeader>
            <InfoContent>
              <ContactInfo>
                <ContactItem>
                  <ContactIcon>
                    <Phone size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>고객센터</ContactLabel>
                    <ContactValue>1588-1234</ContactValue>
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIcon>
                    <Mail size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>이메일</ContactLabel>
                    <ContactValue>support@antsmarket.co.kr</ContactValue>
                  </ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIcon>
                    <MessageSquare size={16} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>카카오톡</ContactLabel>
                    <ContactValue>@antsmarket</ContactValue>
                  </ContactText>
                </ContactItem>
              </ContactInfo>
            </InfoContent>
          </InfoCard>
        </InfoSection>

        <FAQSection>
          <FAQTitle>자주 묻는 질문</FAQTitle>
          <FAQList>
            {frequentFAQs.map((faq, index) => (
              <FAQItem key={index} onClick={() => handleFAQClick(faq)}>
                <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
                  <FAQCategory>{faq.category}</FAQCategory>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                </div>
                <ChevronRight size={16} color="#9ca3af" />
              </FAQItem>
            ))}
          </FAQList>
        </FAQSection>

        <StatsSection>
          <StatCard>
            <StatNumber color="#3b82f6">24시간</StatNumber>
            <StatLabel>평균 응답 시간</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber color="#10b981">98%</StatNumber>
            <StatLabel>고객 만족도</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber color="#f59e0b">1,247</StatNumber>
            <StatLabel>이번 달 해결</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber color="#ef4444">365일</StatNumber>
            <StatLabel>연중무휴 서비스</StatLabel>
          </StatCard>
        </StatsSection>
      </Container>
    </ContentLayout>
  );
}

export default Support;